<script setup lang="ts">
import { AppRepository } from '@sortera/dvfv-persistence'
import { computed } from 'vue'

import { UiEnvBanner } from '@/components/ui/UiEnvBanner/index.ts'
// eslint-disable-next-line no-restricted-imports
import { compressImage, convertHeicToJpg, isHeic, uploadImages } from '@/integrations/image-utils/api.ts'
import { useProvideImageUtils } from '@/integrations/image-utils/index.ts'
import { UiRouterView, useAuthorizeNavigation } from '@/services/routing/index.ts'
import { guessDefaultLocale, switchLanguage } from '@/services/translation/index.ts'
import { getBackendEnvName, getFirebaseEnvName } from '@/utils/env.ts'

const { VITE_ENVIRONMENT_NAME } = import.meta.env

useAuthorizeNavigation()

const appRepo = new AppRepository()

const { hasUpdate } = appRepo.useHasUpdate('dv')

function refreshApp(): void {
  window.location.reload()
}

const env = computed(() => ({
  frontendEnv: VITE_ENVIRONMENT_NAME,
  backendEnv: getBackendEnvName(),
  firebaseEnv: getFirebaseEnvName(),
}))

switchLanguage(guessDefaultLocale())

useProvideImageUtils({ uploadImages, compressImage, convertHeicToJpg, isHeic })
</script>

<template>
  <UiEnvBanner v-bind="env" />
  <div
    v-if="hasUpdate"
    class="bg-base-100 absolute z-30 flex w-full flex-col items-center p-2"
  >
    <button
      class="btn btn-primary btn-lg"
      @click="refreshApp"
    >
      {{ $t('general.new-version') }}
    </button>
  </div>
  <Suspense>
    <UiRouterView />
  </Suspense>
</template>

<style>
@import '@/assets/global.css';

@reference '@/assets/tailwind.css';

* {
  /* Enable panning and pinch zoom gestures, but disable additional non-standard gestures such as double-tap to zoom. */
  touch-action: manipulation;
}

html[data-theme='winter'] {
  @apply min-h-screen bg-zinc-200 bg-cover;
  background: url('/images/bg.png') no-repeat fixed center center;
}

html[data-theme='business'] {
  @apply min-h-screen bg-zinc-800 bg-cover;
  background: url('/images/bg-dark.png') no-repeat fixed center center;
}

.text-shadow {
  color: white; /* TODO: black text with black shadow looks blurry, improve this */
  text-shadow: 1px 1px 2px black;
}

.my-overlay {
  @apply fixed top-0 left-0 h-screen w-screen bg-zinc-800/30 dark:bg-zinc-700/60;
}

h1,
.h1 {
  @apply flex items-center gap-2 font-mono text-2xl font-semibold;
}

h2,
.h2 {
  @apply flex items-center gap-2 font-mono text-xl font-medium;
}

h3,
.h3 {
  @apply flex items-center gap-2 font-mono text-lg;
}

h4,
.h4 {
  @apply flex items-center gap-2 font-sans text-base;
}

.slide-enter-active {
  animation: slide-from-below 200ms cubic-bezier(0.77, 0, 0.175, 1);
}
.slide-leave-active {
  animation: slide-from-below 200ms cubic-bezier(0.77, 0, 0.175, 1) reverse;
}
@keyframes slide-from-below {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-y-enter-active {
  animation: fade-y-in 0.3s ease-in-out;
}

@keyframes fade-y-in {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-y-leave-active {
  animation: fade-y-out 0.1s ease-out;
}

@keyframes fade-y-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0.5;
    transform: translateY(5px);
  }
}

.fade-enter-active {
  animation: fade-in 0.3s ease-in-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-leave-active {
  animation: fade-out 0.1s ease-out;
}

@keyframes fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.5;
  }
}

/* Increase DaisyUIs input font-sizes for better accessibility.
 When less than 16px, WebKit browsers will simply do an auto-zoom whenever the input is focused. */
.textarea,
.input-sm {
  @apply text-base;
}
</style>
